import React, { useMemo } from "react";

import { templates } from "@reactivated";

import { IProduct } from "../../../models/catalogue";
import { isoWebPageURL } from "../../../models/nominals";
import { check } from "../../../models/utils";
import { useSSRStorePrefill } from "../../../store/reducers";
import { StickyConfigurator } from "../StickyConfigurator";
import {
    IUpsellModalComponentFn,
    ModularConfiguratorOptionSet,
} from "../models";
import { prefillSSRStoreData } from "../ssr";

interface IStickyConfiguratorSSRProps
    extends templates.StickyConfiguratorTemplate {
    // Optional site-specific overrides
    financingLink?: string;
    showUpsellModal?: (product: IProduct | null) => boolean;
    getUpsellModalComponentFn?: (
        product: IProduct,
    ) => IUpsellModalComponentFn | null;
    upsellModal?: IUpsellModalComponentFn;
    onBaseVariantChanged?: (baseVariant: IProduct | null) => void;
    strikeThroughMSRP?: boolean;
    actualPriceStyle?: string;
    overrideFinancingCopy?: string;
}

export const StickyConfiguratorSSR = (
    props: IStickyConfiguratorSSRProps,
): React.JSX.Element => {
    // Parse configurator option set for initial_variant_id and product_ids
    const optionSet = useMemo(
        () =>
            check(
                ModularConfiguratorOptionSet.decode(
                    JSON.parse(props.configurator_init_data),
                ),
            ),
        [props.configurator_init_data],
    );

    // Hydrate Redux store with SSR data before render
    useSSRStorePrefill(
        {
            products_data: props.products_data,
            product_class_data: props.product_class_data,
            financing_plans_data: props.financing_plans_data,
            concrete_bundles_data: props.concrete_bundles_data,
            upsell_info_modals_data: props.upsell_info_modals_data,
            promo_cards_data: props.promo_cards_data,
            reviews_products_data: props.reviews_products_data,
            initial_variant_id: optionSet.initial_variant_id,
            product_ids: optionSet.product_ids,
        },
        prefillSSRStoreData,
    );

    return (
        <StickyConfigurator
            basketLink={isoWebPageURL.wrap(props.basket_link)}
            configureGiftsLink={isoWebPageURL.wrap(props.configure_gifts_link)}
            financingLink={
                props.financingLink
                    ? isoWebPageURL.wrap(props.financingLink)
                    : undefined
            }
            isPLCVersion={props.is_plc_version}
            categorySelectors={props.category_selectors_data}
            stickyTriggerSelector={props.sticky_trigger_selector}
            editScrollTargetSelector={props.edit_scroll_target_selector}
            showUpsellModal={props.showUpsellModal}
            getUpsellModalComponentFn={props.getUpsellModalComponentFn}
            upsellModal={props.upsellModal}
            onBaseVariantChanged={props.onBaseVariantChanged}
            strikeThroughMSRP={props.strikeThroughMSRP}
            actualPriceStyle={props.actualPriceStyle}
            overrideFinancingCopy={props.overrideFinancingCopy}
        />
    );
};
